// The Certify external-client seams (ADR-D46 decisions-adr.md:105; slice-plan.md:60-68).
//
// Types + schemas only here; the implementations land in packages/storage (ObjectStorageClient,
// behind `storage.scaleway_real`) and packages/certify (the verifier + health clients, behind
// `certify.real_cert_verify` / `certify.real_health_checks`) in a later wave. Application code calls
// these interfaces, never a provider SDK directly (app-engineering §3), so "works locally" equals
// "works in CI" and a provider swap is one adapter.
//
// SSRF WALL (app-security §2 A01 - SSRF is an access-control failure). Every seam that resolves or
// fetches a NETWORK target requires a PARSED target (`PublicHostname`), so a raw attacker-controlled
// string cannot reach the seam. Syntactic parsing is NECESSARY BUT NOT SUFFICIENT: a public name can
// still resolve to a private IP (DNS rebinding), so the IMPL MUST resolve-and-deny any private,
// loopback, or link-local address (including the 169.254.169.254 metadata endpoint) BEFORE connecting.
// The type pins the OBLIGATION in the signature; enforcement is impl-side and re-checked in review.
import { z } from "zod";
/**
 * A hostname the boundary has PARSED as syntactically public-SHAPED, branded so a raw string cannot
 * be passed to a fetch/health seam (parse, don't validate). This is a SYNTACTIC PRE-FILTER ONLY, NOT
 * an SSRF wall: the regex requires a dotted name with a letter TLD, so a dotted-quad IP literal (e.g.
 * `169.254.169.254`) and a bare single label (`localhost`, `metadata`) are rejected here - but a
 * syntactically public name can still resolve to a private IP (DNS rebinding), and multi-label
 * internal FQDNs (`metadata.google.internal`, `foo.localhost`, `x.svc.cluster.local`, `*.nip.io`)
 * are public-SHAPED. The REAL wall is impl-side: resolve-and-deny every private/loopback/link-local
 * address (incl. 169.254.169.254) with a DNS-rebinding pin, per the SSRF WALL note above.
 *
 * As cheap DEFENSE-IN-DEPTH (not the wall), a denylist rejects known-internal TLD suffixes here so
 * the obvious multi-label internal names never even reach the resolve-and-deny step. The impl still
 * owns the wall - a suffix denylist can never be complete (it needs runtime DNS resolution).
 */
const INTERNAL_TLD_SUFFIXES = [".internal", ".localhost", ".local", ".svc", ".nip.io"];
export const PublicHostname = z
    .string()
    .trim()
    .min(1)
    .max(253)
    .regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i, "must be a public DNS hostname (not an IP, loopback, or link-local name)")
    .refine((host) => !INTERNAL_TLD_SUFFIXES.some((suffix) => host.toLowerCase().endsWith(suffix)), "must not be a known-internal name (.internal/.localhost/.local/.svc/.nip.io)")
    .brand();
//# sourceMappingURL=certify.js.map